import type { PageDimensions } from '../../core';
import type { RotationDeps, RotationSlice } from './types';

// ---------------------------------------------------------------------------
// Page rotation reader — used by consumers outside this feature via barrel
// ---------------------------------------------------------------------------

type PageGeometryState = {
  rotation?: Pick<RotationSlice['rotation'], 'values' | 'animations'>;
};

export const getPageRotation = (
  state: PageGeometryState,
  pageId: number
): number =>
  state.rotation?.animations[pageId]?.visualRotation ??
  state.rotation?.values[pageId] ??
  0;

// ---------------------------------------------------------------------------
// Target-rotation-aware dimensions
// ---------------------------------------------------------------------------

/**
 * Target-rotation-aware width, fixed max height.
 *
 * Width swaps based on the committed or in-flight target rotation so that
 * horizontal layout adapts when a page is rotated. Height is always
 * `max(w, h)` — this guarantees vertical space never changes during
 * rotation, so pages below never shift vertically.
 *
 * proxy-memoize only tracks `rotation.values[pageId]` (changes on commit)
 * and `rotation.animations[pageId]?.targetRotation` (changes once at
 * animation start), so layout never recomputes per-frame.
 */
export const getTargetRotationDimensions = (
  pageId: number,
  baseDims: PageDimensions,
  state: RotationDeps & RotationSlice
): PageDimensions => {
  const committed = state.rotation.values[pageId] ?? 0;
  const target = state.rotation.animations[pageId]?.targetRotation ?? committed;
  const isSwapped = target % 180 !== 0;

  return {
    width: isSwapped ? baseDims.height : baseDims.width,
    height: Math.max(baseDims.width, baseDims.height),
  };
};

/**
 * Compute the max effective page width for the current rotation state.
 * Used to determine contentWidth for camera adjustment.
 */
export const getMaxEffectiveWidth = (
  state: RotationDeps & RotationSlice
): number => {
  const { order, list } = state.pageLayout;
  if (order.length === 0) return 0;

  return Math.max(
    ...order.map(pageId => {
      const page = list[pageId];
      if (!page) return 0;
      return getTargetRotationDimensions(pageId, page.dimensions, state).width;
    })
  );
};

export const calculateEndRotation = (
  baseRotation: number,
  logicalRotation: number,
  degrees: number
) =>
  degrees > 0
    ? Math.min(baseRotation + degrees, logicalRotation + 360)
    : Math.max(baseRotation + degrees, logicalRotation - 360);
